import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Webhook } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { CsvButton, PeriodPicker, Progress, Stat, ROLE_NAME, currentPeriod } from "./salesUi";

function GatewayCard() {
  const { data, reload } = useFetch("/sales/paymongo");
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const register = async () => {
    setBusy(true);
    try { const r = await api.post("/sales/paymongo/register-webhook"); toast.success(`Webhook registered: ${r.data.url}`); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="surface flex flex-wrap items-center justify-between gap-3 p-5" data-testid="paymongo-setup-card">
      <div>
        <p className="flex items-center gap-2 text-sm font-bold"><Webhook size={15} /> PayMongo</p>
        <p className="text-xs text-slate-500" data-testid="paymongo-status">
          {data.configured ? `Connected · ${data.mode}` : "Not configured — add the PayMongo secret key"}
          {data.webhook ? ` · Webhook: ${data.webhook.url}` : data.env_webhook_secret ? " · Webhook secret set" : " · Webhook not registered"}
        </p>
      </div>
      {data.configured && <button className="btn-secondary btn-sm" disabled={busy} onClick={register} data-testid="register-webhook-button">{data.mode === "LIVE" ? "Register live webhook (care.giahealthcare.ph)" : "Register test webhook for this site"}</button>}
    </div>
  );
}

function PerformanceTable({ period }) {
  const { data } = useFetch(`/sales/performance?period=${period}`);
  if (!data) return <Spinner />;
  const rows = data.rows.map((r) => ({
    name: r.user.full_name, role: ROLE_NAME[r.user.role], manager: r.user.manager_name || "", active: r.user.active,
    personal_target: r.personal.target_amount, personal_achieved: r.personal.achieved_amount, personal_pct: r.personal.pct_amount ?? "",
    personal_count: r.personal.achieved_count, team_target: r.team?.target_amount ?? "", team_achieved: r.team?.achieved_amount ?? "", team_pct: r.team?.pct_amount ?? "",
  }));
  return (
    <div className="surface overflow-x-auto" data-testid="performance-table-card">
      <div className="flex items-center justify-between p-4"><p className="text-sm font-bold">Performance by manager & agent · {period}</p><CsvButton name={`giacare-performance-${period}.csv`} rows={rows} /></div>
      <table className="data-table" data-testid="performance-table">
        <thead><tr><th>Name</th><th>Role</th><th>Team</th><th>Personal</th><th>%</th><th>Team total</th><th>%</th></tr></thead>
        <tbody>{data.rows.map((r) => (
          <tr key={r.user.id} data-testid={`perf-row-${r.user.id}`}>
            <td className="font-semibold">{r.user.full_name}</td><td className="text-xs">{ROLE_NAME[r.user.role]}</td><td className="text-xs">{r.user.role === "sales_manager" ? "—" : r.user.manager_name}</td>
            <td className="text-xs">{money(r.personal.achieved_amount)} / {money(r.personal.target_amount)} · {r.personal.achieved_count}</td><td className="text-xs font-bold">{r.personal.pct_amount ?? "—"}</td>
            <td className="text-xs">{r.team ? `${money(r.team.achieved_amount)} / ${money(r.team.target_amount)}` : "—"}</td><td className="text-xs font-bold">{r.team?.pct_amount ?? "—"}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const SalesFeed = ({ items }) => (
  <div className="surface overflow-x-auto">
    <p className="p-4 text-sm font-bold">Recent PayMongo payments</p>
    <table className="data-table" data-testid="recent-sales-table">
      <thead><tr><th>Reference</th><th>Card</th><th>Client</th><th>Seller</th><th>Amount</th><th>Status</th><th>Created</th></tr></thead>
      <tbody>{items.map((p) => (
        <tr key={p.id}><td className="font-mono text-xs">{p.reference}</td><td className="font-mono text-xs">{p.card_serial}</td><td className="text-xs">{p.purchaser?.name}</td>
          <td className="text-xs">{p.seller_name}</td><td className="font-semibold">{money(p.amount)}</td><td><StatusBadge status={p.status} /></td><td className="text-xs">{fmtDateTime(p.created_at)}</td></tr>
      ))}</tbody>
    </table>
  </div>
);

export default function SalesDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(currentPeriod());
  const { data } = useFetch(`/sales/dashboard?period=${period}`);
  const admin = ["admin", "super_admin"].includes(user.role);
  if (!data) return <Spinner />;
  return (
    <div className="space-y-6">
      <PageHead eyebrow="GIAcare+ Sales Force" title={admin ? "Sales performance" : `Hi, ${user.full_name.split(" ")[0]}`}
        sub="Only PayMongo-verified payments count as sales. Monthly periods in Philippine time."
        actions={<div className="flex gap-2"><PeriodPicker value={period} onChange={setPeriod} />{!admin && <Link to="/sales/new" className="btn-primary" data-testid="new-sale-button"><Plus size={15} /> New sale</Link>}</div>} />
      {!data.gateway.configured && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="gateway-not-configured">PayMongo is not configured yet, so new sales cannot be started.</p>}
      {admin && <GatewayCard />}
      {admin && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Verified sales (all time)" value={money(data.all_time.amount)} sub={`${data.all_time.count} memberships`} testId="stat-all-time" />
            <Stat label="Active managers / agents" value={`${data.managers} / ${data.agents}`} sub={data.pending_approvals ? `${data.pending_approvals} awaiting approval` : "No pending approvals"} testId="stat-team" />
            <Stat label="Registered clients" value={data.registered_clients} sub={`${data.active_memberships} active memberships`} testId="stat-clients" />
            <Stat label="Pending payments" value={data.pending} sub={`Legacy cash: ${money(data.legacy_cash.amount)} (${data.legacy_cash.count})`} testId="stat-pending" />
          </div>
          <Progress label={`Company target · ${period}`} p={data.company} testId="company-progress" />
          <PerformanceTable period={period} />
        </>
      )}
      {data.personal && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Progress label="My personal target" p={data.personal} testId="personal-progress" />
          {data.team && <Progress label="Team target" p={data.team} testId="team-progress" />}
        </div>
      )}
      {data.personal && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Stat label="Today" value={money(data.today.amount)} sub={`${data.today.count} sold`} testId="stat-today" />
          <Stat label="This week" value={money(data.week.amount)} sub={`${data.week.count} sold`} testId="stat-week" />
          <Stat label={user.role === "sales_manager" ? "Team this month" : "This month"} value={money(data.month.amount)} sub={`${data.month.count} sold · ${data.pending} pending`} testId="stat-month" />
          {data.team ? <Stat label="Unallocated team target" value={money(data.unallocated.amount)} sub={`${data.unallocated.count} memberships · ${data.agents} agents`} testId="stat-unallocated" />
            : <Stat label="My clients" value={data.clients} testId="stat-my-clients" />}
        </div>
      )}
      {user.role === "sales_manager" && <PerformanceTable period={period} />}
      <SalesFeed items={data.recent_sales} />
      {admin && data.recent_activations.length > 0 && (
        <div className="surface p-4" data-testid="recent-activations">
          <p className="mb-2 text-sm font-bold">Recent activations</p>
          {data.recent_activations.map((c) => <p key={c.serial} className="flex justify-between border-b border-slate-100 py-1.5 text-xs"><span className="font-mono">{c.serial} · {c.purchaser?.name}</span><span>{fmtDateTime(c.activated_at)}</span></p>)}
        </div>
      )}
    </div>
  );
}
